import { useEffect, useRef } from 'react'
import { Outlet, useLocation } from 'react-router-dom'

import { layoutCopy } from '../content/layout'
import { HTML_LANG, langForPath } from '../i18n'
import { useSeo } from '../hooks/useSeo'
import Footer from './Footer'
import Header from './Header'
import JsonLd from './JsonLd'

/**
 * The shell every page renders inside.
 *
 * Google Analytics is the one third party on this site, loaded for every visitor by the
 * snippet in index.html, and there is no consent banner. Nothing routing-related is done
 * for it here on purpose: GA4's enhanced measurement already counts each route change
 * off the history event, and a custom page_view hook beside it double-counts every
 * navigation (verified, not theorised). Nothing else may be added quietly either: the
 * privacy page names what the site loads and what it sets, and it is a legal document.
 * The fonts stay self-hosted.
 */
export default function RootLayout() {
  const { pathname, hash } = useLocation()
  const lang = langForPath(pathname)
  const t = layoutCopy[lang]
  const main = useRef<HTMLElement>(null)

  useSeo(pathname)

  useEffect(() => {
    document.documentElement.lang = HTML_LANG[lang]
  }, [lang])

  // The header nav points at sections of the home page, so a client-side navigation can
  // arrive carrying a fragment. The browser only scrolls to a fragment on a real page
  // load, so do it here; without a fragment, go back to the top the way a new page
  // should.
  useEffect(() => {
    if (hash) {
      const target = document.getElementById(hash.slice(1))
      if (target) {
        target.scrollIntoView({ block: 'start' })
        return
      }
    }
    window.scrollTo(0, 0)
  }, [pathname, hash])

  return (
    <>
      <a
        href="#main"
        className="skipLink"
        onClick={(event) => {
          // Moving focus as well as the viewport: a fragment link alone leaves the
          // keyboard where it was, so the next Tab goes back into the header.
          event.preventDefault()
          main.current?.focus()
          main.current?.scrollIntoView()
        }}
      >
        {t.skipToContent}
      </a>
      <Header lang={lang} />
      <main id="main" ref={main} tabIndex={-1}>
        <Outlet />
      </main>
      <Footer lang={lang} />
      <JsonLd pathname={pathname} />
    </>
  )
}
